import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useStockLocationContext } from "../context/StockLocationContext";
import { useCustomerContext } from "../context/customerContext";
import Spinner from "./Spinner";
import { fetchScanHistoryByIDWithItems } from "../utils/api";
import { ScanHistory, Product } from "../utils/types";

interface EnrichedScanHistoryItem {
  id: number;
  item_id: string;
  product_id: number;
  quantity: number;
  product_name: string;
  product_media: { original_url: string; preview_url: string }[];
}

const ScanHistoryDetails: React.FC = () => {
  const { locations } = useStockLocationContext();
  const { customers } = useCustomerContext();
  const { id } = useParams<{ id: string }>();
  const { movement_type: routeMovementType } = useParams<{ movement_type: string }>();
  const scanHistoryId = id ? parseInt(id, 10) : null;

  const [scanHistoryDetails, setScanHistoryDetails] = useState<ScanHistory | null>(null);
  const [scanHistoryItems, setScanHistoryItems] = useState<EnrichedScanHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getLocationName = (locationId: number | string) => {
    const id = Number(locationId);
    const location = locations.find((loc) => loc.id === id);
    return location ? location.name : `ID#${id}`;
  };

  const getUsername = (userId: number) => {
    const customer = customers.find((c) => c.user_id === userId);
    return customer ? customer.name : `User ID #${userId}`;
  };

  useEffect(() => {
    if (!scanHistoryId || !routeMovementType) {
      setError("Missing Scan History ID or movement type.");
      setLoading(false);
      return;
    }

    const loadAllDetails = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchScanHistoryByIDWithItems(scanHistoryId, routeMovementType);
        setScanHistoryDetails(data);
        setScanHistoryItems(data.items || []);
      } catch (err: any) {
        setError(err.message || "Failed to load scan history details.");
      } finally {
        setLoading(false);
      }
    };

    loadAllDetails();
  }, [scanHistoryId, routeMovementType]);


  if (loading) {
    return <Spinner />;
  }

  if (error) return <div className="text-red-600 p-4 text-center">{error}</div>;

  if (!scanHistoryDetails) {
    return (
      <div className="p-4 text-center text-gray-500">
        No scan history found for ID: {scanHistoryId}.
      </div>
    );
  }

  const {
    id: shId,
    subject,
    user_id,
    stock_location_id,
    status,
    confirmed_by,
    movement_type,
    scanned_at,
    confirmed_at,
    created_at,
    updated_at
  } = scanHistoryDetails;

  const renderDetail = (label: string, value: React.ReactNode) => (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-green-700 sm:mt-0 sm:col-span-2">
        {value ?? <span className="italic text-green-600">N/A</span>}
      </dd>
    </div>
  );

  // Total unique items
  const totalItems = scanHistoryItems.length;

  // Total quantity (sum of all item.quantity)
  const totalQty = scanHistoryItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );


  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Scan History Details #{shId}</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <dl className="grid grid-cols-3 gap-4 divide-y divide-gray-200 p-6">
          {renderDetail(
            "Status",
            <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status === "completed"
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
                }`}
            >
              {status.toUpperCase()}
            </span>
          )}
          {renderDetail("Movement", movement_type.toUpperCase())}
          {renderDetail("Scanned by User", getUsername(user_id))}
          {renderDetail("Location ID", getLocationName(stock_location_id))}
          {renderDetail("Scanned At", scanned_at ? new Date(scanned_at).toLocaleString() : "N/A")}
          {renderDetail("Confirmed At", confirmed_at ? new Date(confirmed_at).toLocaleString() : "N/A")}
          {renderDetail("Confirmed By", confirmed_by)}
          {renderDetail("Created At", new Date(created_at).toLocaleString())}
          {renderDetail("Last Updated", new Date(updated_at).toLocaleString())}
          {renderDetail("Subject", subject)}
        </dl>
      </div>

      <h2 className="text-xl font-bold mt-6 mb-4">
        Scanned Items ({scanHistoryItems.length})
      </h2>
      <div className="bg-white shadow-md rounded-lg">
        <ul className="divide-y divide-gray-200">
          {scanHistoryItems.length > 0 ? (
            scanHistoryItems.map((item) => (
              <li key={item.id} className="p-4 flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <img
                    className="h-16 w-16 rounded-md object-cover"
                    src={
                      item.product_media.length > 0
                        ? item.product_media[0].preview_url
                        : window.APP_CONFIG?.PUBLIC_URL + "/placeholder.jpg"
                    }
                    alt={item.product_name}
                  />
                </div>
                <div className="flex justify-between flex-1">
                  <div>
                    <span className="font-semibold text-gray-800">{item.product_name}</span>
                    <span className="block text-sm text-gray-500">
                      PID#: {item.product_id}
                    </span>
                  </div>
                  {/* <span className="text-sm text-gray-600">
                    <strong>Item ID:</strong> {item.item_id}
                  </span> */}
                  <div>
                  <span className="text-sm text-gray-600">
                    <strong>Qty:</strong> {item.quantity}
                  </span>
                  </div>
                </div>

              </li>
            ))
          ) : (
            <li className="p-4 text-gray-500 text-center">
              No items were scanned in this session.
            </li>
          )}
        </ul>
      </div>

      <div className="p-4 flex justify-end items-right space-x-4 text-sm md:text-xl text-gray-600 font-semibold">
          <span>
            <strong>Total Item:</strong> {totalItems}
          </span>
          <span>
            <strong>Total Qty:</strong> {totalQty}
          </span>
      </div>

    </div>
  );
};

export default ScanHistoryDetails;
